import {
  canViewCommunity,
  commentsToFlatNodes,
  communityRSSUrl,
  defaultPostListingMode,
  editComment,
  editPersonNotes,
  editPost,
  enableNsfw,
  handleWarnComment,
  handleWarnPost,
  mixedToCommentSortType,
  mixedToPostSortType,
  reportToast,
  setIsoData,
  updateCommunityBlock,
  updatePersonBlock,
} from "@utils/app";
import {
  getQueryParams,
  getQueryString,
  resourcesSettled,
  bareRoutePush,
  numToSI,
} from "@utils/helpers";
import { scrollMixin } from "../mixins/scroll-mixin";
import type { ItemIdAndRes, QueryParams } from "@utils/types";
import { itemLoading, RouteDataResponse } from "@utils/types";
import { Component, InfernoNode, RefObject, createRef } from "inferno";
import { Link, RouteComponentProps, RouterContext } from "inferno-router";
import {
  AddAdmin,
  AddModToCommunity,
  AddModToCommunityResponse,
  BanFromCommunity,
  BanPerson,
  PersonResponse,
  BlockCommunity,
  BlockPerson,
  CommentResponse,
  CommunityResponse,
  CreateComment,
  CreateCommentLike,
  CreateCommentReport,
  CreatePostLike,
  CreatePostReport,
  DeleteComment,
  DeletePost,
  DistinguishComment,
  EditComment,
  EditPost,
  FeaturePost,
  FollowCommunity,
  GetComments,
  PagedResponse,
  CommentView,
  GetCommunity,
  GetCommunityResponse,
  GetPosts,
  PostView,
  GetSiteResponse,
  HidePost,
  LemmyHttp,
  LockPost,
  PostResponse,
  PurgeComment,
  PurgePerson,
  PurgePost,
  RemoveComment,
  RemovePost,
  SaveComment,
  SavePost,
  PostSortType,
  SuccessResponse,
  TransferCommunity,
  CommentSortType,
  MyUserInfo,
  MarkPostAsRead,
  NotePerson,
  EditCommunityNotifications,
  LockComment,
  PostListingMode,
  PaginationCursor,
  PurgeCommunity,
  RemoveCommunity,
  CommentId,
  PostId,
  ModEditPost,
  CommunityTagId,
} from "lemmy-js-client";
import { PostOrCommentType, InitialFetchRequest } from "@utils/types";
import { FirstLoadService, I18NextService } from "../../services";
import { aimooT } from "@utils/aimoo-i18n";
import {
  EMPTY_REQUEST,
  HttpService,
  LOADING_REQUEST,
  RequestState,
  wrapClient,
} from "../../services/HttpService";
import { tippyMixin } from "../mixins/tippy-mixin";
import { toast } from "@utils/app";
import { CommentNodes } from "../comment/comment-nodes";
import { HtmlTags } from "../common/html-tags";
import { Spinner } from "../common/icon";
import { CommentSortDropdown } from "../common/sort-dropdown";
import { SiteSidebar } from "../home/site-sidebar";
import { AimooCommunityPostList } from "./aimoo-community-post-list";
import { PaginatorCursor } from "../common/paginator-cursor";
import { fetchLimit } from "@utils/config";
import { getHttpBaseInternal } from "../../utils/env";
import { CommentsLoadingSkeleton } from "../common/loading-skeleton";
import { CommunitySidebar } from "./community-sidebar";
import { IRoutePropsWithFetch } from "@utils/routes";
import { isBrowser } from "@utils/browser";
import { CommunityHeader } from "./community-header";
import { nowBoolean } from "@utils/date";
import { NoOptionI18nKeys } from "i18next";
import {
  ALL_TIME_INTERVAL,
  Interval,
  intervalFromQuery,
  intervalToQuery,
  intervalToSeconds,
  secondsToLargestInterval,
} from "@components/common/time-interval-filter";
import {
  FilterChipCheckbox,
} from "@components/common/filter-chip-checkbox";
import { removeLocalStorageMarkdown } from "@components/common/markdown-textarea";
import { CommunityTagDropdown } from "@components/common/community-tag-dropdown";
import { amAdmin, amMod } from "@utils/roles";
import classNames from "classnames";

type CommunityData = RouteDataResponse<{
  communityRes: GetCommunityResponse;
  postsRes: PagedResponse<PostView>;
  commentsRes: PagedResponse<CommentView>;
}>;

interface State {
  communityRes: RequestState<GetCommunityResponse>;
  postsRes: RequestState<PagedResponse<PostView>>;
  commentsRes: RequestState<PagedResponse<CommentView>>;
  followCommunityRes: RequestState<CommunityResponse>;
  removeCommunityRes: RequestState<CommunityResponse>;
  addModToCommunityRes: RequestState<AddModToCommunityResponse>;
  purgeCommunityRes: RequestState<SuccessResponse>;
  createCommentRes: ItemIdAndRes<CommentId, CommentResponse>;
  editCommentRes: ItemIdAndRes<CommentId, CommentResponse>;
  voteCommentRes: ItemIdAndRes<CommentId, CommentResponse>;
  votePostRes: ItemIdAndRes<PostId, PostResponse>;
  siteRes: GetSiteResponse;
  showSidebarMobile: boolean;
  isIsomorphic: boolean;
  markPageAsReadLoading: boolean;
  postListingMode: PostListingMode;
  selectButtonsHidden: boolean;
}

interface CommunityProps {
  postOrCommentType: PostOrCommentType;
  sort: PostSortType | CommentSortType;
  time: Interval;
  tagId?: CommunityTagId;
  /** 1-based page for post listings (offset pagination). */
  page: number;
  /** Cursor pagination for comment listings. */
  cursor?: PaginationCursor;
  showHidden?: boolean;
  showRead?: boolean;
  hidePostsWithMedia?: boolean;
}

function getCommunityPageLimit(
  siteRes: GetSiteResponse,
  myUserInfo?: MyUserInfo,
): number {
  // Community feeds follow the site admin setting first (系统设置).
  // User preference is only a fallback — otherwise stale per-user defaults
  // (DB column default 20) override a site setting of e.g. 6 and hide pagination.
  const siteLimit = Number(
    siteRes.site_view.local_site.default_items_per_page,
  );
  const userLimit = Number(
    myUserInfo?.local_user_view.local_user.default_items_per_page,
  );
  const raw = Number.isFinite(siteLimit) && siteLimit > 0
    ? siteLimit
    : Number.isFinite(userLimit) && userLimit > 0
      ? userLimit
      : fetchLimit;
  return Math.min(50, Math.max(1, Math.floor(raw)));
}

function getPageFromQuery(page?: string): number {
  const n = Number(page);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
}

/** Compact page-number list for the Aimoo pager. */
function buildPageNumbers(
  current: number,
  total: number,
): Array<number | "ellipsis"> {
  if (total <= 1) {
    return [1];
  }
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: Array<number | "ellipsis"> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) {
    pages.push("ellipsis");
  }
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  if (end < total - 1) {
    pages.push("ellipsis");
  }
  pages.push(total);
  return pages;
}

type Fallbacks = {
  sort: PostSortType | CommentSortType;
  time: Interval;
  showRead: boolean;
  hidePostsWithMedia: boolean;
};

export function getCommunityQueryParams(
  source: string | undefined,
  siteRes: GetSiteResponse,
  myUserInfo?: MyUserInfo,
) {
  const local_user = myUserInfo?.local_user_view.local_user;
  const local_site = siteRes.site_view.local_site;
  return getQueryParams<CommunityProps, Fallbacks>(
    {
      postOrCommentType: getPostOrCommentTypeFromQuery,
      tagId: getTagIdFromQuery,
      page: getPageFromQuery,
      cursor: (cursor?: string) => cursor,
      sort: getSortTypeFromQuery,
      time: intervalFromQuery,
      showHidden: getShowHiddenFromQuery,
      showRead: getShowReadFromQuery,
      hidePostsWithMedia: getHidePostsWithMediaFromQuery,
    },
    source,
    {
      sort:
        local_user?.default_post_sort_type ?? local_site.default_post_sort_type,
      time:
        secondsToLargestInterval(local_user?.default_post_time_range_seconds) ??
        ALL_TIME_INTERVAL,
      showRead: local_user?.show_read_posts ?? true,
      hidePostsWithMedia: local_user?.hide_posts_with_media ?? false,
    },
  );
}

function getPostOrCommentTypeFromQuery(type?: string): PostOrCommentType {
  return type ? (type as PostOrCommentType) : "post";
}

function getTagIdFromQuery(tag?: string): CommunityTagId | undefined {
  return tag ? Number(tag) : undefined;
}

function getSortTypeFromQuery(
  type: string | undefined,
  fallback: PostSortType | CommentSortType,
): PostSortType | CommentSortType {
  return type ? (type as PostSortType | CommentSortType) : fallback;
}

function getShowHiddenFromQuery(hidden: string | undefined): boolean {
  return hidden === "true";
}

function getShowReadFromQuery(
  showRead: string | undefined,
  fallback: boolean,
): boolean {
  return showRead ? showRead === "true" : fallback;
}

function getHidePostsWithMediaFromQuery(
  hidePostsWithMedia: string | undefined,
  fallback: boolean,
): boolean {
  return hidePostsWithMedia ? hidePostsWithMedia === "true" : fallback;
}

type CommunityPathProps = { name: string };
type CommunityRouteProps = RouteComponentProps<CommunityPathProps> &
  CommunityProps;
export type CommunityFetchConfig = IRoutePropsWithFetch<
  CommunityData,
  CommunityPathProps,
  CommunityProps
>;

@scrollMixin
@tippyMixin
export class Community extends Component<CommunityRouteProps, State> {
  private isoData = setIsoData<CommunityData>(this.context);
  state: State = {
    communityRes: EMPTY_REQUEST,
    postsRes: EMPTY_REQUEST,
    commentsRes: EMPTY_REQUEST,
    followCommunityRes: EMPTY_REQUEST,
    removeCommunityRes: EMPTY_REQUEST,
    addModToCommunityRes: EMPTY_REQUEST,
    purgeCommunityRes: EMPTY_REQUEST,
    createCommentRes: { id: 0, res: EMPTY_REQUEST },
    editCommentRes: { id: 0, res: EMPTY_REQUEST },
    voteCommentRes: { id: 0, res: EMPTY_REQUEST },
    votePostRes: { id: 0, res: EMPTY_REQUEST },
    siteRes: this.isoData.siteRes,
    showSidebarMobile: false,
    isIsomorphic: false,
    markPageAsReadLoading: false,
    postListingMode: defaultPostListingMode(this.isoData),
    selectButtonsHidden: true,
  };
  private readonly mainContentRef: RefObject<HTMLDivElement>;

  loadingSettled() {
    return resourcesSettled([
      this.state.communityRes,
      this.props.postOrCommentType === "post"
        ? this.state.postsRes
        : this.state.commentsRes,
    ]);
  }

  constructor(props: CommunityRouteProps, context: object) {
    super(props, context);

    this.mainContentRef = createRef();
    // Only fetch the data if coming from another route
    if (FirstLoadService.isFirstLoad) {
      const { communityRes, commentsRes, postsRes } = this.isoData.routeData;

      this.state = {
        ...this.state,
        isIsomorphic: true,
        commentsRes,
        communityRes,
        postsRes,
      };
    }
  }

  fetchCommunityToken?: symbol;
  async fetchCommunity(props: CommunityRouteProps) {
    const token = (this.fetchCommunityToken = Symbol());
    this.setState({ communityRes: LOADING_REQUEST });
    const name = decodeURIComponent(props.match.params.name);
    const communityRes = await HttpService.client.getCommunity({
      name,
    });
    if (token === this.fetchCommunityToken) {
      this.setState({ communityRes });
    }
  }

  async componentWillMount() {
    if (!this.state.isIsomorphic && isBrowser()) {
      await Promise.all([
        this.fetchCommunity(this.props),
        this.fetchData(this.props),
      ]);
    }
  }

  async componentWillReceiveProps(
    nextProps: CommunityRouteProps & { children?: InfernoNode },
  ) {
    const nameChanged =
      this.props.match.params.name !== nextProps.match.params.name;
    const reload = bareRoutePush(this.props, nextProps) || nameChanged;

    if (reload) {
      await this.fetchCommunity(nextProps);
    }

    const shouldRefetch =
      reload ||
      this.props.postOrCommentType !== nextProps.postOrCommentType ||
      this.props.page !== nextProps.page ||
      this.props.cursor !== nextProps.cursor ||
      this.props.sort !== nextProps.sort ||
      this.props.time !== nextProps.time ||
      this.props.showHidden !== nextProps.showHidden ||
      this.props.showRead !== nextProps.showRead ||
      this.props.tagId !== nextProps.tagId ||
      this.props.hidePostsWithMedia !== nextProps.hidePostsWithMedia;

    if (shouldRefetch) {
      await this.fetchData(nextProps);
    }
  }

  static fetchInitialData = async ({
    headers,
    query: {
      postOrCommentType,
      cursor,
      page,
      sort,
      time,
      showHidden,
      showRead,
      tagId,
      hidePostsWithMedia,
    },
    match: { params: props },
    site,
    myUserInfo,
  }: InitialFetchRequest<
    CommunityPathProps,
    CommunityProps
  >): Promise<CommunityData> => {
    const client = wrapClient(
      new LemmyHttp(getHttpBaseInternal(), { headers }),
    );

    const communityName = decodeURIComponent(props.name);
    const communityForm: GetCommunity = {
      name: communityName,
    };
    const limit = getCommunityPageLimit(site, myUserInfo);

    let postsFetch: Promise<RequestState<PagedResponse<PostView>>> =
      Promise.resolve(EMPTY_REQUEST);
    let commentsFetch: Promise<RequestState<PagedResponse<CommentView>>> =
      Promise.resolve(EMPTY_REQUEST);

    if (postOrCommentType === "post") {
      const getPostsForm: GetPosts = {
        community_name: communityName,
        sort: mixedToPostSortType(sort),
        time_range_seconds: intervalToSeconds(time),
        type_: "all",
        show_hidden: showHidden,
        show_read: showRead,
        tag_id: tagId,
        hide_posts_with_media: hidePostsWithMedia,
        limit,
        page: page ?? 1,
      };

      postsFetch = client.getPosts(getPostsForm);
    } else {
      const getCommentsForm: GetComments = {
        community_name: communityName,
        sort: mixedToCommentSortType(sort),
        time_range_seconds: intervalToSeconds(time),
        type_: "all",
        page_cursor: cursor,
        limit,
      };

      commentsFetch = client.getComments(getCommentsForm);
    }

    const communityFetch = client.getCommunity(communityForm);

    const [communityRes, commentsRes, postsRes] = await Promise.all([
      communityFetch,
      commentsFetch,
      postsFetch,
    ]);

    return {
      communityRes,
      commentsRes,
      postsRes,
    };
  };

  updateUrl(props: Partial<CommunityProps>) {
    const {
      postOrCommentType,
      tagId,
      cursor,
      page,
      sort,
      showHidden,
      showRead,
      hidePostsWithMedia,
      time,
      match: {
        params: { name },
      },
    } = {
      ...this.props,
      ...props,
    };

    const queryParams: QueryParams<CommunityProps> = {
      postOrCommentType:
        postOrCommentType && postOrCommentType !== "post"
          ? postOrCommentType
          : undefined,
      page:
        postOrCommentType !== "comment" && page && page > 1
          ? String(page)
          : undefined,
      cursor: postOrCommentType === "comment" ? cursor : undefined,
      sort,
      showHidden: showHidden ? "true" : undefined,
      showRead:
        showRead === undefined ? undefined : showRead ? "true" : "false",
      tagId: tagId?.toString(),
      hidePostsWithMedia: hidePostsWithMedia ? "true" : undefined,
      time: intervalToQuery(time),
    };

    const search = getQueryString(queryParams);
    this.props.history.push({
      pathname: `/c/${name}`,
      search,
    });
  }

  /** Build community listing URL for a target page (used by Link pager). */
  pageHref(targetPage: number): string {
    const {
      postOrCommentType,
      tagId,
      sort,
      showHidden,
      showRead,
      hidePostsWithMedia,
      time,
      match: {
        params: { name },
      },
    } = this.props;

    const queryParams: QueryParams<CommunityProps> = {
      postOrCommentType:
        postOrCommentType && postOrCommentType !== "post"
          ? postOrCommentType
          : undefined,
      page: targetPage > 1 ? String(targetPage) : undefined,
      sort,
      showHidden: showHidden ? "true" : undefined,
      showRead:
        showRead === undefined ? undefined : showRead ? "true" : "false",
      tagId: tagId?.toString(),
      hidePostsWithMedia: hidePostsWithMedia ? "true" : undefined,
      time: intervalToQuery(time),
    };

    return `/c/${name}${getQueryString(queryParams)}`;
  }

  fetchDataToken?: symbol;
  async fetchData(props: CommunityRouteProps) {
    const token = (this.fetchDataToken = Symbol());
    const {
      postOrCommentType,
      cursor,
      page,
      sort,
      time,
      showHidden,
      showRead,
      tagId,
      hidePostsWithMedia,
    } = props;
    const name = decodeURIComponent(props.match.params.name);
    const limit = getCommunityPageLimit(
      this.isoData.siteRes,
      this.isoData.myUserInfo,
    );

    if (postOrCommentType === "post") {
      this.setState({ postsRes: LOADING_REQUEST, commentsRes: EMPTY_REQUEST });
      const postsRes = await HttpService.client.getPosts({
        sort: mixedToPostSortType(sort),
        time_range_seconds: intervalToSeconds(time),
        type_: "all",
        community_name: name,
        show_hidden: showHidden,
        show_read: showRead,
        tag_id: tagId,
        hide_posts_with_media: hidePostsWithMedia,
        limit,
        page: page ?? 1,
      });
      if (token === this.fetchDataToken) {
        this.setState({ postsRes });
      }
    } else {
      this.setState({ commentsRes: LOADING_REQUEST, postsRes: EMPTY_REQUEST });
      const commentsRes = await HttpService.client.getComments({
        sort: mixedToCommentSortType(sort),
        type_: "all",
        community_name: name,
        page_cursor: cursor,
        limit,
      });
      if (token === this.fetchDataToken) {
        this.setState({ commentsRes });
      }
    }
  }

  get currentRes() {
    if (this.props.postOrCommentType === "post") {
      return this.state.postsRes;
    } else {
      return this.state.commentsRes;
    }
  }

  get documentTitle(): string {
    const cRes = this.state.communityRes;
    return cRes.state === "success"
      ? `${cRes.data.community_view.community.title} - ${this.isoData.siteRes.site_view.site.name}`
      : "";
  }

  renderCommunity() {
    const res =
      this.state.communityRes.state === "success" &&
      this.state.communityRes.data;
    return (
      <>
        {res && (
          <HtmlTags
            title={this.documentTitle}
            context={this.context as RouterContext}
            canonicalPath={res.community_view.community.ap_id}
            description={res.community_view.community.summary}
            image={res.community_view.community.icon}
          />
        )}
        {this.communityInfo()}
      </>
    );
  }

  render() {
    const res =
      this.state.communityRes.state === "success" &&
      this.state.communityRes.data;
    const canViewCommunity_ = res && canViewCommunity(res.community_view);

    return (
      <div className="aimoo-community-detail">
        <div className="aimoo-community-detail__inner container-lg px-3">
          {res && <ShowWarning res={res} />}
          {canViewCommunity_ ? (
            <>
              {this.renderCommunity()}
              <div className="aimoo-community-detail__layout">
                <div
                  className="aimoo-community-detail__main"
                  ref={this.mainContentRef}
                >
                  {this.selects()}
                  {this.listings()}
                  <div className="aimoo-community-detail__pager">
                    {this.pagination()}
                    {this.markPageAsReadButton()}
                  </div>
                  {this.mobileSidebar(canViewCommunity_)}
                </div>
                <aside className="aimoo-community-detail__aside d-none d-lg-block">
                  {this.sidebar()}
                </aside>
              </div>
            </>
          ) : (
            res && (
              <>
                {this.renderCommunity()}
                <div
                  className="alert alert-danger text-bg-danger"
                  role="alert"
                >
                  <h4 className="alert-heading">
                    {I18NextService.i18n.t("community_visibility_private")}
                  </h4>
                  <div className="card-text">
                    {I18NextService.i18n.t(
                      "cant_view_private_community_message",
                    )}
                  </div>
                </div>
                {this.mobileSidebar(true)}
              </>
            )
          )}
        </div>
      </div>
    );
  }

  mobileSidebar(forceShow?: boolean) {
    const showSidebar = forceShow || this.state.showSidebarMobile;
    return (
      <div className="d-block d-lg-none">
        {showSidebar && this.sidebar()}
      </div>
    );
  }

  markPageAsReadButton(): InfernoNode {
    const { postOrCommentType } = this.props;
    const { postsRes, markPageAsReadLoading } = this.state;
    const myUserInfo = this.isoData.myUserInfo;

    if (markPageAsReadLoading) return <Spinner />;

    const haveUnread =
      postOrCommentType === "post" &&
      postsRes.state === "success" &&
      postsRes.data.items.some(p => !p.post_actions?.read_at);

    if (!haveUnread || !myUserInfo) return undefined;
    return (
      <div className="my-2">
        <button
          className="btn btn-light border-light-subtle"
          onClick={() => handleMarkPageAsRead(this, myUserInfo)}
        >
          {I18NextService.i18n.t("mark_page_as_read")}
        </button>
      </div>
    );
  }

  pagination(): InfernoNode {
    const { postOrCommentType, page, cursor } = this.props;

    if (postOrCommentType === "comment") {
      return (
        <PaginatorCursor
          current={cursor}
          resource={this.currentRes}
          onPageChange={nextCursor =>
            this.updateUrl({ cursor: nextCursor, page: 1 })
          }
        />
      );
    }

    const limit = getCommunityPageLimit(
      this.isoData.siteRes,
      this.isoData.myUserInfo,
    );
    const currentPage = page || 1;
    const postsRes = this.state.postsRes;
    const itemCount =
      postsRes.state === "success" ? postsRes.data.items.length : 0;
    const communityPosts =
      this.state.communityRes.state === "success"
        ? this.state.communityRes.data.community_view.community.posts
        : 0;

    // Prefer community.posts for total pages; only guess "maybe more" when
    // the counter is missing and the current page is full.
    let totalPages = Math.max(1, Math.ceil(communityPosts / limit) || 1);
    if (communityPosts <= 0 && itemCount >= limit) {
      totalPages = Math.max(totalPages, currentPage + 1);
    }
    totalPages = Math.max(totalPages, currentPage);

    const hasNext = currentPage < totalPages;
    const hasPrev = currentPage > 1;

    // Always show the pager when there is more than one page, or the user
    // is already past page 1 (so they can navigate back).
    if (totalPages <= 1 && !hasPrev) {
      return undefined;
    }

    const pages = buildPageNumbers(currentPage, totalPages);
    const scrollMain = () => {
      if (isBrowser() && this.mainContentRef.current) {
        this.mainContentRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    };

    return (
      <nav
        className="aimoo-pager aimoo-pager--bottom"
        aria-label={aimooT("pager_aria")}
      >
        <div className="aimoo-pager__meta">
          {aimooT("page_of", { current: currentPage, total: totalPages })}
          <span className="aimoo-pager__meta-sep">·</span>
          {aimooT("per_page", { limit })}
          <span className="aimoo-pager__meta-sep">·</span>
          {aimooT("total_items", { count: numToSI(communityPosts) })}
        </div>
        <div className="aimoo-pager__controls">
          {hasPrev ? (
            <Link
              className="aimoo-pager__btn"
              to={this.pageHref(currentPage - 1)}
              onClick={scrollMain}
            >
              {aimooT("prev_page")}
            </Link>
          ) : (
            <span className="aimoo-pager__btn aimoo-pager__btn--disabled">
              {aimooT("prev_page")}
            </span>
          )}
          <div className="aimoo-pager__pages">
            {pages.map((entry, index) =>
              entry === "ellipsis" ? (
                <span key={`ellipsis-${index}`} className="aimoo-pager__ellipsis">
                  …
                </span>
              ) : entry === currentPage ? (
                <span
                  key={entry}
                  className="aimoo-pager__page aimoo-pager__page--active"
                  aria-current="page"
                >
                  {entry}
                </span>
              ) : (
                <Link
                  key={entry}
                  className="aimoo-pager__page"
                  to={this.pageHref(entry)}
                  onClick={scrollMain}
                >
                  {entry}
                </Link>
              ),
            )}
          </div>
          {hasNext ? (
            <Link
              className="aimoo-pager__btn"
              to={this.pageHref(currentPage + 1)}
              onClick={scrollMain}
            >
              {aimooT("next_page")}
            </Link>
          ) : (
            <span className="aimoo-pager__btn aimoo-pager__btn--disabled">
              {aimooT("next_page")}
            </span>
          )}
        </div>
      </nav>
    );
  }

  sidebar() {
    if (this.state.communityRes.state !== "success") {
      return undefined;
    }
    const res = this.state.communityRes.data;
    const { siteRes, myUserInfo } = this.isoData;
    // For some reason, this returns an empty vec if it matches the site langs
    const communityLangs =
      res.discussion_languages.length === 0
        ? siteRes.all_languages.map(({ id }) => id)
        : res.discussion_languages;

    return (
      <>
        <CommunitySidebar
          communityView={res.community_view}
          moderators={res.moderators}
          admins={siteRes.admins}
          enableNsfw={enableNsfw(siteRes)}
          allLanguages={siteRes.all_languages}
          siteLanguages={siteRes.discussion_languages}
          communityLanguages={communityLangs}
          myUserInfo={this.isoData.myUserInfo}
          hideButtons
          aimooLayout
          onFollow={form => handleFollow(this, form, myUserInfo)}
          onBlock={form => handleBlockCommunity(this, form, myUserInfo)}
          onEditNotifs={form => handleEditCommunityNotifs(form)}
          onRemove={form => handleRemoveCommunity(this, form)}
          onPurge={form => handlePurgeCommunity(this, form)}
          removeLoading={this.state.removeCommunityRes.state === "loading"}
          purgeLoading={this.state.purgeCommunityRes.state === "loading"}
          followLoading={this.state.followCommunityRes.state === "loading"}
        />
        {!res.community_view.community.local && res.site && (
          <SiteSidebar site={res.site} myUserInfo={this.isoData.myUserInfo} />
        )}
      </>
    );
  }

  listings(): InfernoNode | void {
    const { postOrCommentType } = this.props;
    const { siteRes, myUserInfo } = this.isoData;

    if (postOrCommentType === "post") {
      switch (this.state.postsRes.state) {
        case "loading":
          return (
            <AimooCommunityPostList posts={[]} loading />
          );
        case "success":
          return (
            <AimooCommunityPostList
              posts={this.state.postsRes.data.items}
            />
          );
      }
    } else {
      if (this.state.communityRes.state !== "success") {
        return;
      }
      switch (this.state.commentsRes.state) {
        case "loading":
          return <CommentsLoadingSkeleton />;
        case "success":
          return (
            <CommentNodes
              nodes={commentsToFlatNodes(this.state.commentsRes.data.items)}
              viewType={"flat"}
              createLoading={itemLoading(this.state.createCommentRes)}
              editLoading={itemLoading(this.state.editCommentRes)}
              voteLoading={itemLoading(this.state.voteCommentRes)}
              fetchChildrenLoading={undefined}
              isTopLevel
              showContext
              showCommunity={false}
              hideImages={false}
              showMarkRead={"hide"}
              showBadgeForPostCreator={false}
              muteCommunityName
              mutePersonName={false}
              hideAvatar={false}
              markReadLoading={undefined}
              admins={siteRes.admins}
              allLanguages={siteRes.all_languages}
              siteLanguages={siteRes.discussion_languages}
              myUserInfo={this.isoData.myUserInfo}
              localSite={siteRes.site_view.local_site}
              onSaveComment={form => handleSaveComment(this, form)}
              onBlockPerson={form => handleBlockPerson(form, myUserInfo)}
              onBlockCommunity={form =>
                handleBlockCommunity(this, form, myUserInfo)
              }
              onDeleteComment={form => handleDeleteComment(this, form)}
              onRemoveComment={form => handleRemoveComment(this, form)}
              onCommentVote={form => handleCommentVote(this, form)}
              onCommentReport={form => handleCommentReport(form)}
              onDistinguishComment={form =>
                handleDistinguishComment(this, form)
              }
              onAddModToCommunity={form => handleAddModToCommunity(this, form)}
              onAddAdmin={form => handleAddAdmin(this, form)}
              onTransferCommunity={form => handleTransferCommunity(this, form)}
              onPurgeComment={form => handlePurgeComment(this, form)}
              onPurgePerson={form => handlePurgePerson(this, form)}
              onBanPersonFromCommunity={form =>
                handleBanFromCommunity(this, form)
              }
              onBanPerson={form => handleBanPerson(this, form)}
              onCreateComment={form => handleCreateComment(this, form)}
              onEditComment={form => handleEditComment(this, form)}
              onPersonNote={form => handlePersonNote(this, form)}
              onLockComment={form => handleLockComment(this, form)}
              onWarnComment={form => handleWarnComment(form)}
              onMarkRead={() => {}}
              onFetchChildren={() => {}}
            />
          );
      }
    }
  }

  communityInfo() {
    const res =
      (this.state.communityRes.state === "success" &&
        this.state.communityRes.data) ||
      undefined;
    const community = res && res.community_view.community;
    const communityView = res && res.community_view;
    const urlCommunityName = this.props.match.params.name;
    const myUserInfo = this.isoData.myUserInfo;
    const canPost =
      !!communityView &&
      (!communityView.community.posting_restricted_to_mods ||
        amMod(communityView) ||
        amAdmin(myUserInfo));

    return (
      <CommunityHeader
        community={community}
        communityView={communityView || undefined}
        urlCommunityName={urlCommunityName}
        myUserInfo={myUserInfo}
        followLoading={this.state.followCommunityRes.state === "loading"}
        canPost={canPost}
        onFollow={() =>
          community &&
          handleFollow(this, { community_id: community.id, follow: true }, myUserInfo)
        }
        onUnFollow={() =>
          community &&
          handleFollow(
            this,
            { community_id: community.id, follow: false },
            myUserInfo,
          )
        }
      />
    );
  }

  selects() {
    const res =
      this.state.communityRes.state === "success" &&
      this.state.communityRes.data;
    const {
      postOrCommentType,
      sort,
      showHidden,
      showRead,
      tagId,
      hidePostsWithMedia,
    } = this.props;
    const communityRss = res
      ? communityRSSUrl(res.community_view.community, sort)
      : undefined;
    const { selectButtonsHidden } = this.state;

    const myUserInfo = this.isoData.myUserInfo;
    const tags = res && res.community_view.tags;
    const postCount = res ? res.community_view.community.posts : 0;
    const isHotSort =
      sort === "hot" ||
      sort === "active" ||
      sort === "scaled" ||
      sort === "top";
    const showTagFilter =
      postOrCommentType === "post" && !!tags && tags.length > 0;
    const showCommentSort = postOrCommentType !== "post";

    return (
      <>
        {postOrCommentType === "post" && (
          <div className="aimoo-community-detail__toolbar">
            <div className="aimoo-community-detail__sort" role="tablist">
              <button
                type="button"
                className={classNames("aimoo-community-detail__sort-btn", {
                  "aimoo-community-detail__sort-btn--active": sort === "new",
                })}
                onClick={() => handleSortChange(this, "new")}
              >
                {aimooT("sort_new")}
              </button>
              <button
                type="button"
                className={classNames("aimoo-community-detail__sort-btn", {
                  "aimoo-community-detail__sort-btn--active": isHotSort,
                })}
                onClick={() => handleSortChange(this, "hot")}
              >
                {aimooT("sort_hot")}
              </button>
            </div>
            <span className="aimoo-community-detail__post-count">
              {aimooT("total_posts", { count: numToSI(postCount) })}
            </span>
          </div>
        )}

        {(showTagFilter || showCommentSort) && (
          <div className="aimoo-community-detail__filters row row-cols-auto align-items-center g-1 mb-3">
            {showTagFilter && (
              <div className="col">
                <CommunityTagDropdown
                  tags={tags}
                  currentOption={tagId?.toString() ?? "0"}
                  onSelect={val => handleCommunityTagFilterChange(this, val)}
                />
              </div>
            )}
            {showCommentSort && (
              <div className="col">
                <CommentSortDropdown
                  currentOption={mixedToCommentSortType(sort)}
                  onSelect={val => handleCommentSortChange(this, val)}
                  showLabel
                />
              </div>
            )}
          </div>
        )}
        {/* Keep RSS alternate link for crawlers without showing the icon. */}
        {communityRss && (
          <link
            rel="alternate"
            type="application/atom+xml"
            href={communityRss}
          />
        )}
        {postOrCommentType === "post" && myUserInfo && !selectButtonsHidden && (
          <div className="row row-cols-auto mt-2 mb-3">
            <div className="col">
              <FilterChipCheckbox
                option={"show_hidden_posts"}
                isChecked={showHidden ?? false}
                onCheck={hidden => handleShowHiddenChange(this, hidden)}
              />
            </div>
            <div className="col">
              <FilterChipCheckbox
                option={"hide_read_posts"}
                isChecked={!(showRead ?? false)}
                onCheck={hideRead => handleHideReadChange(this, hideRead)}
              />
            </div>
            <div
              className="col"
              data-tippy-content={I18NextService.i18n.t(
                "hide_memes_description",
              )}
            >
              <FilterChipCheckbox
                option={"hide_memes"}
                isChecked={hidePostsWithMedia ?? false}
                onCheck={hidePostsWithMedia =>
                  handlePostsWithMediaChange(this, hidePostsWithMedia)
                }
              />
            </div>
          </div>
        )}
      </>
    );
  }
}

async function handleMarkPageAsRead(
  i: Community,
  myUserInfo: MyUserInfo | undefined,
) {
  const { postOrCommentType } = i.props;
  const { postsRes } = i.state;

  const post_ids =
    postOrCommentType === "post" &&
    postsRes.state === "success" &&
    postsRes.data.items
      .filter(p => !p.post_actions?.read_at)
      .map(p => p.post.id);

  if (post_ids && post_ids.length) {
    i.setState({ markPageAsReadLoading: true });
    const res = await HttpService.client.markManyPostsAsRead({
      read: true,
      post_ids,
    });
    if (res.state === "success") {
      i.setState(s => {
        if (s.postsRes.state === "success") {
          s.postsRes.data.items.forEach(p => {
            if (post_ids.includes(p.post.id) && myUserInfo) {
              if (!p.post_actions) {
                p.post_actions = {};
              }
              p.post_actions.read_at = nowBoolean(true);
            }
          });
        }
        return { postsRes: s.postsRes, markPageAsReadLoading: false };
      });
    } else {
      i.setState({ markPageAsReadLoading: false });
    }
  }
}

function handleSortChange(i: Community, sort: PostSortType) {
  i.updateUrl({ sort, cursor: undefined, page: 1 });
}

function handleCommentSortChange(i: Community, sort: CommentSortType) {
  i.updateUrl({ sort, cursor: undefined, page: 1 });
}

function handleCommunityTagFilterChange(i: Community, tag: string) {
  // A zero is an "All" / undefined
  const tagId = tag === "0" ? undefined : Number(tag);

  i.updateUrl({ tagId, cursor: undefined, page: 1 });
}

function handleShowHiddenChange(i: Community, showHidden: boolean) {
  i.updateUrl({
    showHidden,
    cursor: undefined,
    page: 1,
  });
}

function handleHideReadChange(i: Community, hideRead: boolean) {
  i.updateUrl({
    showRead: !hideRead,
    cursor: undefined,
    page: 1,
  });
}

function handlePostsWithMediaChange(i: Community, hidePostsWithMedia: boolean) {
  i.updateUrl({
    hidePostsWithMedia,
    cursor: undefined,
    page: 1,
  });
}

async function handleAddModToCommunity(i: Community, form: AddModToCommunity) {
  i.setState({ addModToCommunityRes: LOADING_REQUEST });
  const addModToCommunityRes = await HttpService.client.addModToCommunity(form);
  i.setState({ addModToCommunityRes });

  updateModerators(i, addModToCommunityRes);
  if (addModToCommunityRes.state === "success") {
    toast(I18NextService.i18n.t(form.added ? "appointed_mod" : "removed_mod"));
  }
}

async function handleFollow(
  i: Community,
  form: FollowCommunity,
  myUserInfo: MyUserInfo | undefined,
) {
  i.setState({ followCommunityRes: LOADING_REQUEST });
  const followCommunityRes = await HttpService.client.followCommunity(form);
  i.setState({ followCommunityRes });

  updateCommunity(i, followCommunityRes);

  // Update myUserInfo
  if (followCommunityRes.state === "success") {
    const communityId = followCommunityRes.data.community_view.community.id;
    const mui = myUserInfo;
    if (mui) {
      mui.follows = mui.follows.filter(i => i.community.id !== communityId);
    }
  }
}

async function handlePurgePerson(i: Community, form: PurgePerson) {
  const purgePersonRes = await HttpService.client.purgePerson(form);
  purgeItem(i, purgePersonRes);
}

async function handlePurgeComment(i: Community, form: PurgeComment) {
  const purgeCommentRes = await HttpService.client.purgeComment(form);
  purgeItem(i, purgeCommentRes);
}

async function handlePurgePost(i: Community, form: PurgePost) {
  const purgeRes = await HttpService.client.purgePost(form);
  purgeItem(i, purgeRes);
}

async function handleBlockCommunity(
  i: Community,
  form: BlockCommunity,
  myUserInfo: MyUserInfo | undefined,
) {
  const blockCommunityRes = await HttpService.client.blockCommunity(form);
  if (blockCommunityRes.state === "success") {
    updateCommunityBlock(blockCommunityRes.data, form.block, myUserInfo);
    i.setState(s => {
      if (s.communityRes.state === "success" && myUserInfo) {
        const cv = s.communityRes.data.community_view;
        if (!cv.community_actions) {
          cv.community_actions = {};
        }
        cv.community_actions.blocked_at = nowBoolean(form.block);
      }
    });
  }
}

async function handleBlockPerson(
  form: BlockPerson,
  myUserInfo: MyUserInfo | undefined,
) {
  const blockPersonRes = await HttpService.client.blockPerson(form);
  if (blockPersonRes.state === "success") {
    updatePersonBlock(blockPersonRes.data, form.block, myUserInfo);
  }
}

async function handleEditCommunityNotifs(form: EditCommunityNotifications) {
  const res = await HttpService.client.editCommunityNotifications(form);
  if (res.state === "success") {
    toast(I18NextService.i18n.t("notifications_updated"));
  }
}

async function handleCreateComment(i: Community, form: CreateComment) {
  i.setState({
    createCommentRes: {
      id: form.parent_id ?? 0,
      res: LOADING_REQUEST,
    },
  });
  const res = await HttpService.client.createComment(form);
  i.setState({
    createCommentRes: {
      id: form.parent_id ?? 0,
      res,
    },
  });
  createAndUpdateComments(i, res);

  if (res.state === "failed") {
    toast(I18NextService.i18n.t(res.err.name as NoOptionI18nKeys), "danger");
  }
  return res;
}

async function handleEditComment(i: Community, form: EditComment) {
  i.setState({
    editCommentRes: { id: form.comment_id, res: LOADING_REQUEST },
  });

  const res = await HttpService.client.editComment(form);
  i.setState({
    editCommentRes: { id: form.comment_id, res },
  });

  findAndUpdateCommentEdit(i, res);

  if (res.state === "failed") {
    toast(I18NextService.i18n.t(res.err.name as NoOptionI18nKeys), "danger");
  }
  return res;
}

async function handleDeleteComment(i: Community, form: DeleteComment) {
  const deleteCommentRes = await HttpService.client.deleteComment(form);
  findAndUpdateComment(i, deleteCommentRes);
}

async function handleDeletePost(i: Community, form: DeletePost) {
  const deleteRes = await HttpService.client.deletePost(form);
  findAndUpdatePost(i, deleteRes);
}

async function handleRemovePost(i: Community, form: RemovePost) {
  const removeRes = await HttpService.client.removePost(form);
  findAndUpdatePost(i, removeRes);
}

async function handleRemoveComment(i: Community, form: RemoveComment) {
  const removeCommentRes = await HttpService.client.removeComment(form);
  findAndUpdateComment(i, removeCommentRes);
}

async function handleLockComment(i: Community, form: LockComment) {
  const res = await HttpService.client.lockComment(form);
  findAndUpdateComment(i, res);
}

async function handleSaveComment(i: Community, form: SaveComment) {
  const saveCommentRes = await HttpService.client.saveComment(form);
  findAndUpdateComment(i, saveCommentRes);
}

async function handleSavePost(i: Community, form: SavePost) {
  const saveRes = await HttpService.client.savePost(form);
  findAndUpdatePost(i, saveRes);
}

async function handleFeaturePost(i: Community, form: FeaturePost) {
  const featureRes = await HttpService.client.featurePost(form);
  findAndUpdatePost(i, featureRes);
}

async function handleMarkPostAsRead(
  i: Community,
  form: MarkPostAsRead,
  myUserInfo: MyUserInfo | undefined,
) {
  const res = await HttpService.client.markPostAsRead(form);
  if (res.state === "success") {
    i.setState(s => {
      if (s.postsRes.state === "success") {
        s.postsRes.data.items.forEach(p => {
          if (p.post.id === form.post_id && myUserInfo) {
            if (!p.post_actions) {
              p.post_actions = {};
            }
            p.post_actions.read_at = nowBoolean(form.read);
          }
        });
      }
      return { postsRes: s.postsRes };
    });
  }
}

async function handleCommentVote(i: Community, form: CreateCommentLike) {
  i.setState({ voteCommentRes: { id: form.comment_id, res: LOADING_REQUEST } });
  const res = await HttpService.client.likeComment(form);
  i.setState({ voteCommentRes: { id: form.comment_id, res } });

  findAndUpdateComment(i, res);
}

async function handlePostEdit(i: Community, form: EditPost) {
  const res = await HttpService.client.editPost(form);
  findAndUpdatePost(i, res);
  return res;
}

async function handlePostModEdit(i: Community, form: ModEditPost) {
  const res = await HttpService.client.modEditPost(form);
  findAndUpdatePost(i, res);
  return res;
}

async function handlePostVote(i: Community, form: CreatePostLike) {
  i.setState({ votePostRes: { id: form.post_id, res: LOADING_REQUEST } });
  const res = await HttpService.client.likePost(form);
  i.setState({ votePostRes: { id: form.post_id, res } });

  findAndUpdatePost(i, res);
  return res;
}

async function handleCommentReport(form: CreateCommentReport) {
  const reportRes = await HttpService.client.createCommentReport(form);
  reportToast(reportRes);
}

async function handlePostReport(form: CreatePostReport) {
  const reportRes = await HttpService.client.createPostReport(form);
  reportToast(reportRes);
}

async function handleLockPost(i: Community, form: LockPost) {
  const lockRes = await HttpService.client.lockPost(form);
  findAndUpdatePost(i, lockRes);
}

async function handleHidePost(
  i: Community,
  form: HidePost,
  myUserInfo: MyUserInfo | undefined,
) {
  const hideRes = await HttpService.client.hidePost(form);

  if (hideRes.state === "success") {
    i.setState(prev => {
      if (prev.postsRes.state === "success" && myUserInfo) {
        for (const post of prev.postsRes.data.items.filter(
          p => form.post_id === p.post.id,
        )) {
          if (!post.post_actions) {
            post.post_actions = {};
          }
          post.post_actions.hidden_at = nowBoolean(form.hide);
        }
      }

      return prev;
    });

    toast(I18NextService.i18n.t(form.hide ? "post_hidden" : "post_unhidden"));
  }
}

async function handlePersonNote(i: Community, form: NotePerson) {
  const res = await HttpService.client.notePerson(form);

  if (res.state === "success") {
    i.setState(s => {
      if (s.commentsRes.state === "success") {
        s.commentsRes.data.items = editPersonNotes(
          form.note,
          form.person_id,
          s.commentsRes.data.items,
        );
      }
      if (s.postsRes.state === "success") {
        s.postsRes.data.items = editPersonNotes(
          form.note,
          form.person_id,
          s.postsRes.data.items,
        );
      }
      toast(I18NextService.i18n.t(form.note ? "note_created" : "note_deleted"));
      return s;
    });
  }
}

async function handleDistinguishComment(
  i: Community,
  form: DistinguishComment,
) {
  const distinguishRes = await HttpService.client.distinguishComment(form);
  findAndUpdateComment(i, distinguishRes);
}

async function handleAddAdmin(i: Community, form: AddAdmin) {
  const addAdminRes = await HttpService.client.addAdmin(form);

  if (addAdminRes.state === "success") {
    i.setState(s => ((s.siteRes.admins = addAdminRes.data.admins), s));
  }
}

async function handleTransferCommunity(i: Community, form: TransferCommunity) {
  const transferCommunityRes = await HttpService.client.transferCommunity(form);
  toast(I18NextService.i18n.t("transfer_community"));
  updateCommunityFull(i, transferCommunityRes);
}

async function handleBanFromCommunity(i: Community, form: BanFromCommunity) {
  const banRes = await HttpService.client.banFromCommunity(form);
  updateBanFromCommunity(i, banRes, form.ban);
}

async function handleBanPerson(i: Community, form: BanPerson) {
  const banRes = await HttpService.client.banPerson(form);
  updateBan(i, banRes, form.ban);
}

async function handleRemoveCommunity(i: Community, form: RemoveCommunity) {
  i.setState({ removeCommunityRes: LOADING_REQUEST });
  const removeCommunityRes = await HttpService.client.removeCommunity(form);
  i.setState({ removeCommunityRes });
  updateCommunity(i, removeCommunityRes);
}

async function handlePurgeCommunity(i: Community, form: PurgeCommunity) {
  i.setState({ purgeCommunityRes: LOADING_REQUEST });
  const purgeCommunityRes = await HttpService.client.purgeCommunity(form);
  i.setState({ purgeCommunityRes });
  purgeItem(i, purgeCommunityRes);
}

function updateBanFromCommunity(
  i: Community,
  banRes: RequestState<PersonResponse>,
  banned: boolean,
) {
  // Maybe not necessary
  if (banRes.state === "success") {
    i.setState(s => {
      if (s.postsRes.state === "success") {
        s.postsRes.data.items
          .filter(c => c.creator.id === banRes.data.person_view.person.id)
          .forEach(c => {
            c.creator_banned_from_community = banned;
          });
      }
      if (s.commentsRes.state === "success") {
        s.commentsRes.data.items
          .filter(c => c.creator.id === banRes.data.person_view.person.id)
          .forEach(c => {
            c.creator_banned_from_community = banned;
          });
      }
      return s;
    });
  }
}

function updateBan(
  i: Community,
  banRes: RequestState<PersonResponse>,
  banned: boolean,
) {
  // Maybe not necessary
  if (banRes.state === "success") {
    i.setState(s => {
      if (s.postsRes.state === "success") {
        s.postsRes.data.items
          .filter(c => c.creator.id === banRes.data.person_view.person.id)
          .forEach(c => (c.creator_banned = banned));
      }
      if (s.commentsRes.state === "success") {
        s.commentsRes.data.items
          .filter(c => c.creator.id === banRes.data.person_view.person.id)
          .forEach(c => (c.creator_banned = banned));
      }
      return s;
    });
  }
}

function updateCommunity(i: Community, res: RequestState<CommunityResponse>) {
  i.setState(s => {
    if (s.communityRes.state === "success" && res.state === "success") {
      s.communityRes.data.community_view = res.data.community_view;
      s.communityRes.data.discussion_languages = res.data.discussion_languages;
    }
    return s;
  });
}

function updateCommunityFull(
  i: Community,
  res: RequestState<GetCommunityResponse>,
) {
  i.setState(s => {
    if (s.communityRes.state === "success" && res.state === "success") {
      s.communityRes.data.community_view = res.data.community_view;
      s.communityRes.data.moderators = res.data.moderators;
    }
    return s;
  });
}

function purgeItem(i: Community, purgeRes: RequestState<SuccessResponse>) {
  if (purgeRes.state === "success") {
    toast(I18NextService.i18n.t("purge_success"));
    const context = i.context as RouterContext;
    context.router.history.push(`/`);
  }
}

function findAndUpdateCommentEdit(
  i: Community,
  res: RequestState<CommentResponse>,
) {
  i.setState(s => {
    if (s.commentsRes.state === "success" && res.state === "success") {
      removeLocalStorageMarkdown();
      s.commentsRes.data.items = editComment(
        res.data.comment_view,
        s.commentsRes.data.items,
      );
    }
    return s;
  });
}

function findAndUpdateComment(
  i: Community,
  res: RequestState<CommentResponse>,
) {
  i.setState(s => {
    if (s.commentsRes.state === "success" && res.state === "success") {
      s.commentsRes.data.items = editComment(
        res.data.comment_view,
        s.commentsRes.data.items,
      );
    }
    return s;
  });
}

function createAndUpdateComments(
  i: Community,
  res: RequestState<CommentResponse>,
) {
  i.setState(s => {
    if (s.commentsRes.state === "success" && res.state === "success") {
      removeLocalStorageMarkdown();
      s.commentsRes.data.items.unshift(res.data.comment_view);
    }
    return s;
  });
}

function findAndUpdatePost(i: Community, res: RequestState<PostResponse>) {
  i.setState(s => {
    if (s.postsRes.state === "success" && res.state === "success") {
      removeLocalStorageMarkdown();
      s.postsRes.data.items = editPost(
        res.data.post_view,
        s.postsRes.data.items,
      );
    }
    return s;
  });
}

function updateModerators(
  i: Community,
  res: RequestState<AddModToCommunityResponse>,
) {
  // Update the moderators
  i.setState(s => {
    if (s.communityRes.state === "success" && res.state === "success") {
      s.communityRes.data.moderators = res.data.moderators;
    }
    return s;
  });
}

type ShowWarningProps = { res: GetCommunityResponse };

function ShowWarning({ res }: ShowWarningProps) {
  const community = res.community_view.community;
  const oneWeekAgo = new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000);
  const deadInstance =
    res.site && new Date(res.site?.last_refreshed_at) < oneWeekAgo;
  const deadCommunity = new Date(community.last_refreshed_at) < oneWeekAgo;

  if (!community.local && (deadInstance || deadCommunity)) {
    return <DeadInstanceOrCommunityWarning />;
  }
  return <></>;
}

function DeadInstanceOrCommunityWarning() {
  return (
    <div className="alert alert-warning text-bg-warning" role="alert">
      <h4 className="alert-heading">
        {I18NextService.i18n.t("dead_community_title")}
      </h4>
      <div className="card-text">
        {I18NextService.i18n.t("dead_community_body")}
      </div>
    </div>
  );
}

